// Fetch layer of the Safety Overview. A SOURCE is an overview (`ov:<id>`, served by
// /api/overview — scoped to the caller's projects) or a whole state (`st:<state>`,
// served by /api/safety-overview). Both answer in the overview corridor / segment /
// station shapes, so the page draws them alike.
import { authHeaders } from '../../utils/authHeader';
import { fetchCorridors, fetchSegment, fetchStations, mapLimit } from '../Overview/overviewApi';

async function json(path, params) {
  const qs = new URLSearchParams(Object.entries(params || {}).filter(([, v]) => v != null && v !== '')).toString();
  const res = await fetch(`/api/safety-overview${path}${qs ? `?${qs}` : ''}`, { headers: await authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

export const fetchStates = () => json('/states');

async function send(method, path, body) {
  const res = await fetch(`/api/safety-overview${path}`, {
    method, headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

// road-network elements deleted by hand (Admins / SuperUsers; the server re-checks who may)
export const fetchRoadDeletions = (state) => json('/road-network/deletions', { state });
export const deleteRoadElement = (state, feature, road, reason) => send('POST', '/road-network/deletions', {
  state, road, reason,
  feature: { src: feature.src, type: feature.type, ref: feature.ref, lat: feature.lat, lng: feature.lng, label: feature.label, mp: feature.mp },
});
export const restoreRoadElement = (id) => send('DELETE', `/road-network/deletions/${id}`);
/** The crash payload of one segment (crashes, findings, intersections, cost table, years). */
export const fetchCrashes = (seg, rules) => json('/crashes', { state: seg.state, route: seg.route, path_id: seg.pathId, rules });
export const fetchCmfLibrary = () => json('/cmf-library');
/** The statistical support of one segment (the Safety Dashboard's verdict). */
export const fetchVerdict = (seg, force = false, rules) => json('/verdict', { state: seg.state, route: seg.route, path_id: seg.pathId, force: force ? 1 : null, rules });
/** Runs + metric strip of one direction and analysis. */
export const fetchAnalysis = (seg, direction, analysis, rules) => json('/analysis', { state: seg.state, route: seg.route, path_id: seg.pathId, direction, analysis, rules });
/** Same signature as the Safety Dashboard's, for IntersectionsPanel. */
export const fetchIntersectionsSO = (state, district, rank, limit = 50) => json('/intersections', { state, district, rank, limit });

export const sourceId = (kind, id) => `${kind}:${id}`;
export const parseSource = (id) => {
  const [kind, ...rest] = String(id || '').split(':');
  return kind === 'ov' || kind === 'st' ? { kind, id: kind === 'ov' ? Number(rest.join(':')) : rest.join(':') } : null;
};

/** Every corridor of a source -> {corridors, failed: [labels]} */
export async function sourceCorridors(source, overview) {
  if (source.kind === 'st') {
    const data = await json(`/state/${encodeURIComponent(source.id)}/corridors`);
    return { corridors: data.corridors || [], failed: [] };
  }
  const found = await mapLimit(overview.projects, 3, (p) => fetchCorridors(overview.id, p.id));
  return {
    corridors: found.flatMap((r) => (r.value ? r.value.corridors : [])),
    failed: found.map((r, i) => (r.error ? `project #${overview.projects[i].id}` : null)).filter(Boolean),
  };
}

export async function sourceSegment(source, corridor, rules) {
  if (source.kind === 'st') return (await json(`/state/${encodeURIComponent(source.id)}/segment`, { key: corridor.key, rules })).segment;
  return (await fetchSegment(source.id, corridor.key, corridor.projectId, rules)).segment;
}

export async function sourceStations(source, segment, analysis) {
  if (source.kind === 'st') return json(`/state/${encodeURIComponent(source.id)}/stations`, { key: segment.key, analysis });
  return fetchStations(source.id, segment.key, segment.projectId, analysis);
}

// ---- remembered choices (per browser)
const LAST_KEY = 'safetyOverview:last';
const PINS_KEY = 'safetyOverview:pins';
const read = (k, fallback) => { try { return JSON.parse(localStorage.getItem(k)) ?? fallback; } catch (e) { return fallback; } };
const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private window */ } };

export const rememberSource = (id) => write(LAST_KEY, id);
export const recallSource = () => read(LAST_KEY, null);

/** Remembered map places of one source: [{id, lat, lng, note, created}] */
const PLACES_KEY = 'safetyOverview:places';
export const loadPlaces = (source) => ((read(PLACES_KEY, {}) || {})[source] || []);
export const savePlaces = (source, places) => write(PLACES_KEY, { ...(read(PLACES_KEY, {}) || {}), [source]: places });

/** Pinned rows of one source: {summary: [...keys], clusters: [...], sites: [...]} */
export const loadPins = (source) => ({ summary: [], clusters: [], sites: [], crashClusters: [], program: [], ...((read(PINS_KEY, {}) || {})[source] || {}) });
export const savePins = (source, pins) => write(PINS_KEY, { ...(read(PINS_KEY, {}) || {}), [source]: pins });
